import type { GameMinimalProps, Children } from '../../types';
import type { SwiperOptions } from 'swiper/types/swiper-options';
import type { CardProps } from '../Card/Card';

import * as React from 'react';
import ArrowBackIos from '@mui/icons-material/ArrowBackIos';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownIos from '@mui/icons-material/ArrowDownward';
import { useEditorHandlers } from './@editor';
import { useTheme } from '@mui/material/styles';
import { Card } from '../Card';
import { useAppContext } from '../../contexts/AppContext';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, A11y, Virtual } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import {
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  CardSkeletonStyled,
  CarouselListContainer,
  TrendSticker,
} from './styled';
import WhatshotIcon from '@mui/icons-material/Whatshot';

export type CardPropsOmit = Omit<CardProps, 'src' | 'href' | 'title' | 'alt'>;

export interface ListProps extends SwiperOptions, Children {
  padding?: string | number;
  items: GameMinimalProps[];
  cardOptions: CardPropsOmit;
  slidesPerView: number;
  // arrowOptions?: 'circular' | 'default';
  className?: string;
}

const defaultBreakpoints = {
  // https://mui.com/customization/breakpoints/
  // Try to keep this similar as CollectionGrid breakpoints.
  1200: {
    // lg
    slidesPerView: 6,
    slidesPerGroup: 6,
  },
  900: {
    // md
    slidesPerView: 4,
    slidesPerGroup: 4,
  },
  600: {
    // sm
    slidesPerView: 3,
    slidesPerGroup: 3,
  },
  0: {
    // xs
    slidesPerView: 2,
    slidesPerGroup: 2,
  },
};

export const CarouselList = (props: ListProps) => {
  const {
    items,
    className,
    children,
    padding,
    cardOptions,
    slidesPerView = 6,
    breakpoints = defaultBreakpoints,
    // arrowOptions,
    direction = 'horizontal',
    ...swiperOptions
  } = props;
  const theme = useTheme();
  const appContext = useAppContext();
  const { assetUrl } = appContext.useConfig();
  const channel = React.useRef(new Date().getTime().toString(36));
  const [slides] = React.useState<GameMinimalProps[]>(items || []);

  return (
    <CarouselListContainer
      className={className}
      {...useEditorHandlers('Carousel List')}
      vertical={direction === 'vertical'}
    >
      {children}
      <Swiper
        style={{ padding }}
        id={channel?.current}
        modules={[Navigation, A11y, Virtual]}
        loop={slides?.length > 1}
        slidesPerView={slidesPerView}
        spaceBetween={parseInt(theme.spacing(2).replace('px ', ''))}
        breakpoints={breakpoints}
        a11y={{
          prevSlideMessage: 'Previous slide',
          nextSlideMessage: 'Next slide',
        }}
        navigation={{
          prevEl: `.arrow-left-${channel?.current}`,
          nextEl: `.arrow-right-${channel?.current}`,
        }}
        direction={direction || 'horizontal'}
        {...swiperOptions}
      >
        {slides.map((item: GameMinimalProps, index) => (
          <SwiperSlide key={`${channel?.current}${index}`} virtualIndex={index}>
            {item ? (
              <>
                <Card
                  sx={{ display: 'flex' }}
                  href={`/game/${item.slug}`}
                  src={`${assetUrl}${
                    cardOptions?.template === 'big'
                      ? item.images.cover4x1 || item.images.cover16x9 || item.images.cover
                      : item.images.cover16x9 || item.images.cover
                  }/`}
                  alt={item.name || 'Cover'}
                  title={item.name || undefined}
                  description={item.description || undefined}
                  {...cardOptions}
                />
                {item?.trend && (
                  <TrendSticker>
                    <WhatshotIcon />
                  </TrendSticker>
                )}
              </>
            ) : (
              <CardSkeletonStyled elevation={1} width={cardOptions.width} height={cardOptions.height} />
            )}
          </SwiperSlide>
        ))}
        {direction === 'horizontal' ? (
          <>
            <ArrowLeft
              variant="contained"
              aria-label="Previous"
              color="secondary"
              // arrowOptions={arrowOptions}
              className={`arrow-left-${channel.current}`}
            >
              <ArrowBackIos />
            </ArrowLeft>
            <ArrowRight
              variant="contained"
              aria-label="Next"
              color="secondary"
              // arrowOptions={arrowOptions}
              className={`arrow-right-${channel.current}`}
            >
              <ArrowBackIos />
            </ArrowRight>
          </>
        ) : (
          <>
            <ArrowUp
              variant="contained"
              aria-label="Previous"
              color="secondary"
              // arrowOptions={arrowOptions}
              className={`arrow-left-${channel.current}`}
            >
              <ArrowUpwardIcon />
            </ArrowUp>
            <ArrowDown
              variant="contained"
              aria-label="Next"
              color="secondary"
              // arrowOptions={arrowOptions}
              className={`arrow-right-${channel.current}`}
            >
              <ArrowDownIos />
            </ArrowDown>
          </>
        )}
      </Swiper>
    </CarouselListContainer>
  );
};
